<template lang="pug">
y-dialog(v-if="showDialog" v-model="showDialog" persistent fullscreen scrollable)
  v-card.neutral200
    //- Top bar
    v-card-title.px-4
      y-button(outlined prepend-icon="mdi-arrow-left" color="neutral400" icon-color="neutral700" icon-size="20" size="xs" unset-min-width button-class="px-1" @click="closeDialog")
      div.text-xl.font-semibold.ml-4 Payments
      div.text-xl.font-normal.ml-4 {{ titlePrefix }} {{ `#${displayRid}` }}
      v-spacer
      y-button(label="Done" size="sm" @click="closeDialog")
    v-divider
    v-card-text.neutral300.pa-0
      v-container.pa-6(style="max-width: 1440px;")
        v-row.mx-8(no-gutters)
          v-col(cols="1")
            .text-sm.font-medium.neutral500--text {{ $t('patient') }}
          v-col(cols="2")
            .d-flex.align-center
              div
                v-img.round-4xl(v-if="patient.profile_image" width="20" height="20" :src="$options.filters.smallImage(patient.profile_image.file)" cover)
                  template(v-slot:placeholder)
                    v-row(no-gutters class="fill-height ma-0" align="center" justify="center")
                      v-progress-circular(indeterminate size="15" color="grey" width="2")
                v-card.neutral300.fill-height.round-4xl(v-else flat width="20" height="20")
                  div.d-flex.align-center.justify-center.fill-height
                    v-icon(size="15" x-small) $yzi-user-03
              .text-sm.font-semibold.neutral700--text.ml-2 {{ patient.name }}
          v-col(cols="3")
            billing-info(:billingInfo="invoice.billing_address" :patient="patient" @updated="fetchInvoice")
          v-col(cols="3" offset="1")
            v-row(v-if="hasRegistration" no-gutters)
              v-col(cols="3")
                .text-sm.font-medium.neutral500--text {{ $t('payment') }}
              v-col
                template(v-if="registration.default_payment.panel")
                  .text-sm.neutral700--text.mb-1 {{ registration.default_payment.panel.name }}
                  .text-xs.neutral600--text {{ registration.default_payment.note }}
                .text-sm.neutral700--text(v-else) {{ $t('self_pay') }}
          v-col.text-right(cols="2")
            template(v-if="invoice.paid")
              div.text-sm.neutral700--text.font-semibold.capitalize {{ $t('payment_status') }}
              div.text-3xl.uppercase.font-semibold.neutral700--text {{ $t('paid') }}
            template(v-else)
              div.text-sm.neutral700--text.font-semibold Amount due
              div.text-4xl.font-semibold.neutral700--text.no-wrap {{ totalBalance | displayPrice($currency) }}
              div.text-sm.neutral700--text.font-semibold.mt-2 Paid
            div.d-flex.align-center.justify-end(v-if="totalPaid")
              v-icon.mr-3(size="20" color="success500") $yzi-check-circle-solid
              div.text-2xl.font-semibold.success500--text.no-wrap {{ totalPaid | displayPrice($currency) }}
            y-button.mt-4(label="Record new payment" size="sm" @click="onRecordNewPayment")

        v-card.round-xl.px-1.pt-1.pb-3.mt-4(outlined)
          .data-table
            v-data-table(:headers="headers" :items="transactions" item-key="hid" :items-per-page="-1" hide-default-footer :loading="isFetchingInvoice || isFetchingTransactions" :header-props="{ sortIcon: '$yzi-triangle' }" loader-height="2")
              template(v-slot:item.method="{ item, headers, index }")
                div.d-flex.align-center
                  v-tooltip(top color="black" content-class="black tooltip-top-arrow")
                    template(#activator="{ on, attrs }")
                      div(v-bind="attrs" v-on="on")
                        div.text-sm.neutral700--text.clickable.ellipsis-text-1 {{ item.payment_method }}
                    div.text-sm.neutral100--text {{ item.payment_method }}
                  yz-badge.ml-2(v-if="item.amount < 0" color="yellow-100" text-color="yellow-500" size="sm") Refund
                  template(v-if="item.channel === 'panel'")
                    y-chip.no-wrap.ml-2(v-if="!item.panel" label="Panel" color="grey" size="xs")
                    template(v-else-if="!item.panel_claim")
                      yz-badge.ml-2(v-if="isPendingTransaction(item)" color="neutral-300" text-color="neutral-700" size="sm") Pending
                      yz-badge.ml-2(v-else color="neutral-500" text-color="neutral-100" size="sm") Overdue
                    yz-badge.ml-2(v-else :color="getBadgeColor(item.panel_claim.status)" :text-color="getBadgeTextColor(item.panel_claim.status)" size="sm") {{ item.panel_claim.status | capitalize }}
              template(v-slot:item.date="{ item, headers, index }")
                div.text-sm.neutral700--text.no-wrap(v-if="item.paid_datetime") {{ item.paid_datetime | humanizeDate }}
                div.text-sm.neutral700--text(v-else) -
              template(v-slot:item.amount="{ item, headers, index }")
                div.text-sm.neutral700--text.no-wrap(v-if="item.amount") {{ item.amount | displayPrice($currency) }}
                div.text-sm.neutral700--text(v-else) -
              template(v-slot:item.notes="{ item, headers, index }")
                v-tooltip(v-if="item.internal_description" top color="black" content-class="black tooltip-top-arrow")
                  template(#activator="{ on, attrs }")
                    div.clickable(v-bind="attrs" v-on="on")
                      div.text-sm.neutral700--text.ellipsis-text-1 {{ item.internal_description }}
                  div.text-sm.neutral100--text {{ item.internal_description }}
                div.text-sm.neutral700--text(v-else) -
              template(v-slot:item.images="{ item, headers, index }")
                div.d-flex(v-if="item.documents.length")
                  v-card.round-md.px-2.py-1.clickable.no-wrap(outlined @click="openViewImagesDialog(item)")
                    div.d-flex.align-center
                      v-icon.mr-2(size="16" color="neutral600") $yzi-attachment-01
                      template(v-for="(document, idx) in item.documents")
                        v-avatar(size="16" rounded color="neutral300" :class="idx > 0 ? 'stack-documents' : ''" :key="`dc-${idx}`")
                          template(v-if="document.type.includes('image')")
                            v-img(:src="$options.filters.smallImage(document.file)")
                          v-icon(v-else-if="document.type.includes('pdf')" color="danger500" size="15") mdi-file-pdf-box
                      div.text-xs.font-medium.neutral700--text.ml-2 {{ item.documents.length }} images
              template(v-slot:item.actions="{ item, headers, index }")
                div.d-flex.align-center.justify-end(@click.stop)
                  y-button(outlined height="24" color="neutral400" background-color="var(--v-neutral100)" prepend-icon="$yzi-trash-03" icon-color="danger500" icon-size="16" unset-min-width size="xxs" button-class="px-1" @click="openDeleteDialog(item)")
                  y-button.ml-2(v-if="item.channel === 'panel'" label="Verify" outlined height="24" color="neutral400" background-color="var(--v-neutral100)" text-color="neutral700" append-icon="$yzi-arrow-up-right" icon-size="16" icon-color="neutral700" unset-min-width size="xxs" button-class="px-2" @click="openVerifyPanelDialog(item)")
                  y-button.ml-2(v-if="item.channel === 'self-pay'" label="Print" outlined height="24" color="neutral400" background-color="var(--v-neutral100)" text-color="neutral700" unset-min-width size="xxs" button-class="px-2" :loading="isLoadingDownloadReceipt" @click="onDownloadReceipt(item)")
                  y-button.ml-2(label="Edit" outlined height="24" color="neutral400" background-color="var(--v-neutral100)" text-color="neutral700" unset-min-width size="xxs" button-class="px-2" @click="openRecordPaymentDialog(item)")
            
    .dialogs
      record-payment-dialog(v-if="showRecordPaymentDialog" :open.sync="showRecordPaymentDialog" :transaction="selectedTransaction" @updated="updateTransaction" @closed="selectedTransaction = null")
      view-images-dialog(v-if="showViewImagesDialog" :open.sync="showViewImagesDialog" :images="images" @closed="selectedTransaction = null")
      verify-panel-dialog(v-if="showVerifyPanelDialog" :open.sync="showVerifyPanelDialog" :selectedPanel="selectedTransaction.ref_id" @closed="selectedTransaction = null")
      download-invoice-receipt-dialog(v-if="showSelectReceiptDialog" :open.sync="showSelectReceiptDialog" :documents="receiptTemplates" documentType="receipt" :transaction="selectedTransaction" @closed="selectedTransaction = null")
      y-dialog(v-if="showConfirmationDialog" v-model="showConfirmationDialog" max-width=480 persistent fluid scrollable content-class="round-2xl")
        v-card
          v-card-text.pa-8.primary--text
            .text-md.font-medium.mb-4 Delete this payment?
            .text-sm {{ $t('click_confirm_to_proceed') }}
          v-divider
          v-card-actions.px-6.py-3
            v-spacer
            y-button(@click="showConfirmationDialog = false" size="sm" :label="$t('cancel')" underline)
            y-button(@click="deleteTransaction" :loading="isDeleting" color="danger" :label="$t('confirm')" size="sm" dark)

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import BillingInfo from '@/components/clinic/invoice/BillingInfo.vue'
import RecordPaymentDialog from '@/components/clinic/checkout/RecordPaymentDialog.vue'
import ViewImagesDialog from '@/components/dialogs/ViewImagesDialog.vue'
import VerifyPanelDialog from '@/components/clinic/dialogs/VerifyPanelDialog.vue'
import DownloadInvoiceReceiptDialog from '@/components/clinic/dialogs/DownloadInvoiceReceiptDialog.vue'

export default {
  components: { BillingInfo, RecordPaymentDialog, ViewImagesDialog, VerifyPanelDialog, DownloadInvoiceReceiptDialog },
  props: {
    open: {
      type: Boolean,
    },
    titlePrefix: {
      type: String,
      default: 'Invoice'
    },
    titleRid: {
      type: [String, Number],
      default: ''
    },
    externalRecordPayment: {
      type: Boolean,
      default: false
    },
    activityGroup: {
      type: String,
      default: 'checkout'
    }
  },
  data () {
    return {
      isLoading: false,
      isDeleting: false,
      isFetchingInvoice: false,
      isFetchingTransactions: false,
      isLoadingDownloadReceipt: false,
      showRecordPaymentDialog: false,
      showViewImagesDialog: false,
      showVerifyPanelDialog: false,
      showSelectReceiptDialog: false,
      showConfirmationDialog: false,
      selectedTransaction: null,
      receiptTemplates: [],
      headers: [
        { text: 'Payment type', value: 'method' },
        { text: 'Payment date', value: 'date' },
        { text: 'Amount', value: 'amount' },
        { text: 'Payment notes', value: 'notes' },
        { text: 'Images', value: 'images' },
        { text: '', value: 'actions'},
      ]
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      invoice: 'treatment/getInvoice',
      patient: 'treatment/getPatient',
      registration: 'treatment/getRegistration',
      transactions: 'treatment/getTransactions',
      leadPid: 'treatment/getLeadPid',
      hqSelectedCompany: 'hq/getSelectedCompany',
      isHq: 'hq/getIsHq'
    }),
    showDialog: {
      get () {
        return this.open
      },
      set (newValue) {
        this.$emit('update:open', newValue)
      }
    },
    displayRid () {
      return this.titleRid || this.invoice.rid
    },
    hasRegistration () {
      return !!this.invoice.registration
    },
    totalPaid () {
      if (!this.invoice.pid) {
        return 0
      }
      if (this.transactions.length === 0) {
        return 0
      }
      return this.transactions.reduce((sum, trans) => {
        sum += trans.amount
        return sum
      }, 0)
    },
    totalBalance () {
      if (!this.invoice.pid) {
        return 0
      }
      return this.invoice.total_amount - this.totalPaid
    },
    images () {
      return this.selectedTransaction?.documents.map(m => m.file) || []
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchInvoice () {
      this.isFetchingInvoice = true
      const endpoint = this.isHq ? `/hq/invoices/${this.leadPid}` : `/invoices/${this.leadPid}`
      this.$axios.get(endpoint)
        .then(resp => {
          this.$store.commit('treatment/setInvoice', resp.data)
        })
        .catch(() => {})
        .finally(() => {
          this.isFetchingInvoice = false
        })
    },
    fetchTransactions() {
      this.isFetchingTransactions = true
      let params = {
        company: this.isHq ? this.hqSelectedCompany.id : this.company.id,
        pid: this.leadPid
      }
      const endpoint = this.isHq ? '/hq/transactions' : '/transactions'
      this.$axios.get(endpoint, { params })
        .then(resp => {
          this.$store.commit('treatment/setTransactions', resp.data.results)
        })
        .catch(() => {})
        .finally(() => {
          this.isFetchingTransactions = false
        })
    },
    onRecordNewPayment () {
      if (this.externalRecordPayment) {
        this.$emit('record-payment')
        return
      }
      this.showRecordPaymentDialog = true
    },
    openRecordPaymentDialog (transaction) {
      if (this.externalRecordPayment) {
        this.$emit('edit-payment', transaction)
        return
      }
      this.selectedTransaction = transaction
      this.showRecordPaymentDialog = true
    },
    openViewImagesDialog (transaction) {
      this.selectedTransaction = transaction
      this.showViewImagesDialog = true
    },
    openVerifyPanelDialog (transaction) {
      this.selectedTransaction = transaction
      this.showVerifyPanelDialog = true
    },
    openDeleteDialog (transaction) {
      this.selectedTransaction = transaction
      this.showConfirmationDialog = true
    },
    deleteTransaction () {
      this.isDeleting = true
      const endpoint = `/transactions/${this.selectedTransaction.uid}`
      this.axios.delete(endpoint)
        .then(() => {
          let activity = `Delete payment (#${this.invoice.rid})`
          this.updateOldActivityLog(endpoint, null, null, 'DELETE', this.selectedTransaction.uid, activity)
          const amount = this.$options.filters.displayPrice(this.selectedTransaction.amount, this.$currency)
          this.createActivityLog('delete_transaction', `${this.selectedTransaction.payment_method} amount ${amount} was removed`)
          this.showConfirmationDialog = false
          this.$emit('deleted')
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isDeleting = false
          this.updateTransaction()
        })
    },
    async onDownloadReceipt (item) {
      try {
        this.selectedTransaction = item
        this.isLoadingDownloadReceipt = true
        let params = {
          company: this.company.id,
          categories: 'receipt'
        }
        const { data } = await this.$axios.get('/document_templates', { params })
        if (data.count > 1) {
          this.receiptTemplates = data.results
          this.showSelectReceiptDialog = true
          this.isLoadingDownloadReceipt = false
          return
        }
        if (data.count === 1) {
          this.onPrint(data.results[0])
          return
        }
        this.showMessage(['error', this.$t('error_message.create_item_first', { item: this.$t('receipt').toLowerCase() })])
        this.$router.push('/admin/clinic/settings/document/templates')
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isLoadingDownloadReceipt = false
      }
    },
    async onPrint (item) {
      let key
      try {
        const { data } = await this.$axios.post(`users/${this.user.id}/request_otk`)
        key = data.key
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        this.isLoadingDownloadReceipt = false
        return
      }
      let baseUrl = process.env.VUE_APP_BASE_URL
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        baseUrl = 'https://api-test.yezza.io'
      } else if (process.env.VUE_APP_ENV === 'prod') {
        baseUrl = 'https://export.yezza.io'
      }
      const url = `${baseUrl}/invoices/${this.leadPid}/preview_document?document_template=${item.id}&transaction=${this.selectedTransaction.uid}&key=${key}`
      window.open(url,'_blank')
      this.isLoadingDownloadReceipt = false
      this.selectedTransaction = null
    },
    updateTransaction () {
      this.fetchTransactions()
      this.fetchInvoice()
      this.selectedTransaction = null
    },
    closeDialog () {
      this.$emit('closed')
      this.showDialog = false
    },
    updateOldActivityLog (endpoint, payload, response, method, id, activity) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: this.activityGroup,
        activity: activity,
        raw_data: { endpoint, payload, response, method}
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    },
    createActivityLog (tag, activity) {
      const payload = {
        company: this.company.id,
        app_model: 'form.lead',
        object_id: this.leadPid,
        tag,
        group: this.activityGroup,
        activity
      }
      this.$axios.post('/actions', payload).catch(() => {})
    },
    getBadgeTextColor(status) {
      switch (status) {
        case 'cancelled':
          return 'red-500'
        case 'approved':
          return 'blue-500'
        case 'rejected':
          return 'red-500'
        case 'submitted':
          return 'yellow-500'
        case 'received':
          return 'green-500'
        default:
          return 'neutral-700'
      }
    },
    getBadgeColor(status) {
      switch (status) {
        case 'cancelled':
          return 'red-100'
        case 'approved':
          return 'blue-100'
        case 'rejected':
          return 'red-100'
        case 'submitted':
          return 'yellow-100'
        case 'received':
          return 'green-100'
        default:
          return 'neutral-300'
      }
    },
    isPendingTransaction(transaction) {
      const submissionDue = new Date(transaction.submission_due)
      const now = new Date()
      return now <= submissionDue
    }
  }
}
</script>

<style lang="scss" scoped>
.stack-documents {
  margin-left: -6px;
}
</style>